import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Resets the window scroll to the top on every route change, instantly.
 *
 * React Router v7 preserves the scroll position across navigations, so on mobile
 * you land mid-page after following a nav link. Mounted once in AppLayout, this
 * hook fixes the scroll to (0,0) whenever the pathname changes.
 *
 * ── Et il emmène AUSSI aux ancres, parce que personne d'autre ne le fait ──────
 *
 * Le commentaire d'origine disait que le hook « no-op quand un hash est présent :
 * les navigations à ancre ont leur propre scroll-to-target, qu'il faut
 * préserver ». La première moitié était vraie, la seconde **fausse** : ce scroll
 * n'existait nulle part. Le navigateur ne saute à l'ancre que sur un chargement
 * de page, pas sur une navigation côté client, et React Router ne le fait pas non
 * plus. Tout lien vers `/route#ancre` depuis une AUTRE page atterrissait donc en
 * haut de la page cible, silencieusement.
 *
 * Relevé le 2026-08-04 : les liens `/legal#cgu`, `/legal#mentions` et
 * `/legal#confidentialite` du pied de page étaient dans ce cas depuis toujours,
 * et le lien du panneau de fin de saison vers `/codex#fin-de-saison` a été le
 * premier à rendre le trou visible.
 *
 * Le saut se fait après une frame, pour que la page cible soit montée : sans ça
 * `getElementById` rend null et le scroll ne se produit pas. Les cibles portent
 * déjà leur `scroll-mt-*` pour dégager l'en-tête collant, et le Codex pose
 * `scroll-behavior: smooth` pendant qu'il est monté, donc l'animation reste celle
 * que la page a choisie.
 */
export function useScrollToTop(): void {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1))
      if (!id) return
      // Une frame d'attente : au moment où cet effet tourne, la route cible vient
      // d'être montée mais son contenu n'est pas toujours peint.
      const frame = requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: 'start' })
      })

      return () => cancelAnimationFrame(frame)
    }

    const root = document.documentElement
    const prev = root.style.scrollBehavior
    root.style.scrollBehavior = 'auto'
    window.scrollTo(0, 0)
    root.style.scrollBehavior = prev
    // Le pathname pilote la remise en haut ; le hash est relu à chaque passage,
    // ce qui fait que deux ancres successives sur la même page sautent bien.
  }, [pathname, hash])
}
